<template>
  <div class="temp-humidity-root">
    <AnimadePlaceHolder v-if="loading" height="70px" width="100%" borderRadius="4px" />
    <a
      v-else
      class="card-button th-root-link"
      href="#"
      @click.prevent="goToDetails"
    >
      <div class="temp-humidity-wrap">
        <div
          :class="['th-card', summaryCard.error ? 'th-card--empty' : ageColorClass]"
          title="Ver detalhes do SmartSense"
        >
          <template v-if="summaryCard.error">
            <p class="th-card__empty">{{ summaryCard.message || "SEM DADOS" }}</p>
          </template>
          <template v-else>
            <div class="th-card__lines">
              <p class="th-card__line">{{ formatTempLine(summaryCard.temperature) }}</p>
              <p class="th-card__line th-card__line--humidity">{{ formatHumidityLine(summaryCard.humidity) }}</p>
              <p class="th-card__line th-card__line--date">{{ formatDateLine(summaryCard.readAt) }}</p>
            </div>
          </template>
        </div>
      </div>
    </a>
  </div>
</template>

<script>
import AnimadePlaceHolder from "./AnimadePlaceHolder.vue"
import { isSmartSenseSensor, smartsenseApi } from "../constants/smartsense"
import { format, parseISO } from "date-fns"

export default {
  name: "SensorsTempHumidity",
  components: { AnimadePlaceHolder },
  props: {
    sensors: { type: Array, default: () => [] },
    barn: { type: Object, default: null },
    batch: { type: Array, default: () => [] },
  },
  data() {
    return {
      loading: true,
      summaryCard: { error: true, temperature: null, humidity: null, readAt: null, message: null },
    }
  },
  watch: {
    sensors: {
      handler() {
        this.loadReading()
      },
      deep: true,
    },
  },
  created() {
    this.loadReading()
  },
  computed: {
    ageColorClass() {
      const readAt = this.summaryCard.readAt
      if (!readAt) return "th-card--stale"
      const ageHours = (Date.now() - readAt.getTime()) / (1000 * 60 * 60)
      if (ageHours <= 2) return "th-card--fresh"
      if (ageHours < 48) return "th-card--stale"
      return "th-card--old"
    },
  },
  methods: {
    goToDetails() {
      if (!this.barn) return
      this.$store.commit("setBarnTarget", this.barn)
      this.$store.commit("setBatchTarget", this.batch[0] == undefined ? null : this.batch[0])
      this.$router.push("/batch-details/smart-sense")
    },
    formatTempLine(v) {
      if (v == null || Number.isNaN(Number(v))) return "— ºC"
      return `${Number(v).toFixed(1)} ºC`
    },
    formatHumidityLine(v) {
      if (v == null || Number.isNaN(Number(v))) return "— % UR"
      return `${Number(v).toFixed(1)}% UR`
    },
    formatDateLine(date) {
      if (!date) return "SEM DADOS"
      try {
        return format(date, "dd-MM-yyyy HH:mm")
      } catch {
        return "SEM DADOS"
      }
    },
    parseReadAt(json) {
      if (!json || typeof json !== "object") return null
      const raw =
        json.datetime_created ?? json.created_at ?? json.datetime ?? json.timestamp ?? json.date
      if (raw == null) return null
      try {
        const d = typeof raw === "string" ? parseISO(raw) : new Date(raw)
        return Number.isNaN(d.getTime()) ? null : d
      } catch {
        return null
      }
    },
    pickSensorPk() {
      const list = (this.sensors || []).filter(isSmartSenseSensor)
      const sensor = list[0] || this.sensors[0]
      return sensor?.pk ?? sensor?.id ?? null
    },
    async loadReading() {
      const sensorPk = this.pickSensorPk()
      if (sensorPk == null) {
        this.loading = false
        this.summaryCard = { error: true, temperature: null, humidity: null, message: null }
        return
      }
      this.loading = true
      try {
        this.$store.commit("incrementActiveRequests")
        const url = smartsenseApi.lastData(sensorPk)
        const response = await fetch(url, { headers: this.$store.getters.getHeader })
        if (response.status === 409) {
          this.summaryCard = {
            error: true,
            temperature: null,
            humidity: null,
            message: "SEM LOTE",
          }
          return
        }
        if (!response.ok) {
          this.summaryCard = { error: true, temperature: null, humidity: null, message: null }
          return
        }
        const json = await response.json()
        if (json == null) {
          this.summaryCard = { error: true, temperature: null, humidity: null, message: null }
          return
        }
        const temp = json.temperature != null ? Number(json.temperature) : null
        const hum = json.humidity != null ? Number(json.humidity) : null
        const hasTemp = temp != null && !Number.isNaN(temp)
        const hasHum = hum != null && !Number.isNaN(hum)
        if (!hasTemp && !hasHum) {
          this.summaryCard = { error: true, temperature: null, humidity: null, message: null }
          return
        }
        this.summaryCard = {
          error: false,
          temperature: hasTemp ? temp : null,
          humidity: hasHum ? hum : null,
          readAt: this.parseReadAt(json),
          message: null,
        }
      } catch {
        this.summaryCard = { error: true, temperature: null, humidity: null, message: null }
      } finally {
        this.$store.commit("decrementActiveRequests")
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.temp-humidity-root {
  width: 100%;
}

.th-root-link {
  display: block;
  text-decoration: none;
  color: inherit;
  cursor: pointer;
}

.temp-humidity-wrap {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.th-card {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 70px;
  padding: 10px 10px 12px;
  border-radius: 4px;
  transition: background 0.2s ease;
}

.th-card--fresh {
  background: #a1cd7b;
}

.th-card--fresh:hover {
  background: #5e7d42;
}

.th-card--stale {
  background: #e2bf65;
}

.th-card--stale:hover {
  background: #c59e3a;
}

.th-card--old {
  background: #c25757;
}

.th-card--old:hover {
  background: #952323;
}

.th-card--empty {
  background: #adadad;
}

.th-card--empty:hover {
  background: grey;
}

.th-card__empty {
  margin: 0;
  text-align: center;
  font-size: 14px;
  font-weight: 700;
  color: #fff;
}

.th-card__lines {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  text-align: center;
}

.th-card__line {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  line-height: 1.25;
  color: #fff;
  letter-spacing: -0.02em;
}

.th-card__line--humidity {
  font-size: 15px;
  color: #fff;
}

.th-card__line--date {
  font-size: 13px;
  font-weight: 600;
  color: #fff;
}
</style>
